import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { ConfigProvider, Modal } from "antd5";
import { Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { useUploadBidDocuments } from "lib/hooks/api/bid/bid_document/useUploadBidDocuments";
import { MidPaddingModal } from "lib/themes/MidPaddingModal";
import { EventNames, useTracking } from "lib/tracking";
import UploadDocumentsForm from "../UploadDocumentsForm";
import { RegenerateScorecardModal } from "./ReportModals";

import css from "./UploadMoreDocumentsModal.module.scss";

type BidDocumentUploadForm = {
  files: File[];
};

type Props = {
  bidId: string;
  isOpen: boolean;
  onClose: () => void;
  onRegenerateBidReport: () => void;
};
export default function UploadBidDocumentsModal({
  bidId,
  isOpen,
  onClose,
  onRegenerateBidReport,
}: Props) {
  const { logEvent } = useTracking();
  const [filesProcessing, setFilesProcessing] = useState<boolean>(false);

  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);

  const onFinishUploadingFiles = (_bidId: string) => {
    logEvent(EventNames.additionalDocumentsUploaded, {
      "Action type": "Button clicked",
      "Files added": files.length,
    });

    setIsRegenerateModalOpen(true);
    onClose();
  };

  const { control, watch } = useForm<BidDocumentUploadForm>({
    defaultValues: { files: [] },
    mode: "onChange",
  });

  const { files } = watch();

  const {
    mutateAsync: uploadFiles,
    isLoading: isUploadingFiles,
    uploadStatus,
    setUploadStatus,
  } = useUploadBidDocuments({
    files,
    bidId,
    onComplete: onFinishUploadingFiles,
    setFilesProcessing,
  });

  const isLoading = isUploadingFiles || filesProcessing;

  return (
    <>
      <ConfigProvider theme={MidPaddingModal}>
        <Modal
          open={isOpen}
          closable={false}
          width={680}
          title={<Text h1>Upload document</Text>}
          footer={
            <div className={css.footer}>
              <Button buttonType="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button loading={isLoading} onClick={() => uploadFiles({ bidId })}>
                Upload
              </Button>
            </div>
          }
          onCancel={onClose}
        >
          <div className={css.content}>
            <Text>
              Examples include clarification responses or original bid documents. We'll update the
              bid / no-bid report with the latest information.
            </Text>
            <UploadDocumentsForm
              name="files"
              control={control}
              uploadFileStatus={uploadStatus}
              setUploadFileStatus={setUploadStatus}
              isLoading={isLoading}
            />
          </div>
        </Modal>
      </ConfigProvider>
      <RegenerateScorecardModal
        isOpen={isRegenerateModalOpen}
        modalText="New documents have been uploaded. Would you like to regenerate the report now?"
        onRegenerate={onRegenerateBidReport}
        onClose={() => {
          setIsRegenerateModalOpen(false);
          onClose();
        }}
      />
    </>
  );
}
